import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  Flex,
  Spinner,
  Text,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Grid,
  GridItem,
  FormErrorMessage,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faSearch, faEdit } from "@fortawesome/free-solid-svg-icons";
import DataTable from "../AppDataTable";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as IconButton from "../../themes/IconButtonSet";
import { tableCustomStyles, StatusActive } from "../../themes/TableStyles";
import { StandardHeader } from "../../themes/StandardHeader";

const LivestockTypeManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId, profile } = useAuth();
  const [livestockTypes, setLivestockTypes] = useState([]);
  const [filteredLivestockTypes, setFilteredLivestockTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    LivestockTypeID: "",
    LivestockTypeName: "",
    LivestockTypeNameEN: "",
    Remark: "",
    Status: "1",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});

  // Check if user has permission to edit/add (OfficeType === 'สทช.')
  const hasEditPermission = profile?.OfficeType === "สทช.";

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const columns = [
    {
      name: "รหัสประเภทปศุสัตว์",
      selector: (row) => row.LivestockTypeID,
      sortable: true,
      minWidth: "140px",
    },
    {
      name: "ชื่อประเภทปศุสัตว์",
      selector: (row) => row.LivestockTypeName,
      sortable: true,
      minWidth: "180px",
      grow: 2,
      wrap: true,
    },
    {
      name: "ชื่อประเภทปศุสัตว์ (ภาษาอังกฤษ)",
      selector: (row) => row.LivestockTypeNameEN,
      sortable: true,
      minWidth: "180px",
      grow: 2,
      wrap: true,
      hide: 1024,
    },
    {
      name: "สถานะ",
      selector: (row) => <StatusActive status={row.Status} />,
      sortable: true,
      minWidth: "110px",
    },
    {
      name: "จัดการ",
      cell: (row) => (
        <IconButton.Edit
          icon={faEdit}
          onClick={() => handleEdit(row)}
          label={hasEditPermission ? "แก้ไข" : "รายละเอียด"}
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      minWidth: "120px",
    },
  ];

  const fetchLivestockTypes = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getLivestockTypeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const data = response.LivestockTypeList || [];
        setLivestockTypes(data);
        setFilteredLivestockTypes(data);
      }
    } catch (error) {
      console.error("Error fetching livestock types:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchLivestockTypes();
    }
  }, [sessionId]);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    applyFilters(value, filterStatus);
  };

  const handleStatusFilter = (e) => {
    const value = e.target.value;
    setFilterStatus(value);
    applyFilters(searchTerm, value);
  };

  const applyFilters = (search, status) => {
    let filtered = livestockTypes;

    if (search) {
      const searchTerms = search.split(" ").filter((term) => term.length > 0);
      filtered = filtered.filter((row) =>
        searchTerms.every((term) =>
          [
            row.LivestockTypeID.toLowerCase(),
            row.LivestockTypeName.toLowerCase(),
            row.LivestockTypeNameEN.toLowerCase(),
            row.Status ? "active" : "inactive",
          ].some((field) => field.includes(term))
        )
      );
    }

    if (status) {
      filtered = filtered.filter((row) =>
        status === "1" ? row.Status : !row.Status
      );
    }

    setFilteredLivestockTypes(filtered);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilterStatus("");
    setFilteredLivestockTypes(livestockTypes);
  };

  const handleAddNew = () => {
    setFormData({
      LivestockTypeID: "",
      LivestockTypeName: "",
      LivestockTypeNameEN: "",
      Remark: "",
      Status: "1",
    });
    setIsEditing(false);
    setShowForm(true);
    setErrors({});
  };

  const handleEdit = (row) => {
    setFormData({
      ...row,
      Status: row.Status ? "1" : "0",
    });
    setIsEditing(true);
    setShowForm(true);
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "LivestockTypeID":
      case "LivestockTypeName":
      case "LivestockTypeNameEN":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const isFormValid = () => {
    return (
      formData.LivestockTypeID &&
      formData.LivestockTypeName &&
      formData.LivestockTypeNameEN &&
      Object.keys(errors).length === 0
    );
  };

  const handleSave = async () => {
    if (!hasEditPermission) {
      alert("คุณไม่มีสิทธิ์ในการบันทึกข้อมูล");
      return;
    }
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    setIsLoading(true);
    try {
      const apiUrl = isEditing
        ? "/updateLivestockType"
        : "/createLivestockType";
      const response = await apiService({
        method: "POST",
        url: apiUrl,
        data: {
          ...formData,
          Status: formData.Status === "1",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        alert(isEditing ? "อัปเดตประเภทปศุสัตว์สำเร็จ!" : "สร้างประเภทปศุสัตว์สำเร็จ!");
        setShowForm(false);
        fetchLivestockTypes();
      }
    } catch (error) {
      console.error("Error saving livestock type:", error);
      alert("เกิดข้อผิดพลาดในการบันทึก: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({
      LivestockTypeID: "",
      LivestockTypeName: "",
      LivestockTypeNameEN: "",
      Remark: "",
      Status: "1",
    });
    setErrors({});
  };

  // ไม่ลบข้อมูลออกจากฐานข้อมูล เพราะรหัสประเภทปศุสัตว์ถูกอ้างอิงไว้ในข้อมูลวัคซีนและเอกสารเดิม
  const handleToggleStatus = async () => {
    if (!hasEditPermission) {
      alert("คุณไม่มีสิทธิ์ในการปรับสถานะข้อมูล");
      return;
    }
    const nextStatus = formData.Status !== "1";
    const confirmMessage = nextStatus
      ? "ยืนยันการเปิดการใช้งานประเภทปศุสัตว์นี้ จะกลับมาเลือกใช้ในรายการใหม่ได้อีกครั้ง"
      : "ยืนยันการปิดการใช้งานประเภทปศุสัตว์นี้ จะไม่ปรากฏให้เลือกในรายการใหม่ แต่ข้อมูลเดิมที่อ้างอิงไว้จะยังอยู่ในระบบ";

    if (!window.confirm(confirmMessage)) return;

    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/updateLivestockType",
        data: {
          LivestockTypeID: formData.LivestockTypeID,
          Status: nextStatus,
          UpdatedBy: profile?.DLDEmployeeID || profile?.UserID || "",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        alert(
          nextStatus
            ? "เปิดการใช้งานประเภทปศุสัตว์สำเร็จ"
            : "ปิดการใช้งานประเภทปศุสัตว์สำเร็จ"
        );
        setShowForm(false);
        fetchLivestockTypes();
      }
    } catch (error) {
      console.error("Error updating livestock type status:", error);
      alert("เกิดข้อผิดพลาดในการปรับสถานะ: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!sessionId) {
    return null;
  }

  return (
    <Box
      p={{ base: 3, sm: 4, md: 8, lg: 12 }}
      m={{ base: 2, md: 6, lg: 10 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={{ base: 4, md: 6 }} align="stretch">
        <StandardHeader
          title="รายการข้อมูลประเภทปศุสัตว์"
          bgColor="iBlue.600"
          icon={faFileLines}
        />

        {/* ส่วนตัวกรอง */}
        <Flex
          w="100%"
          gap={3}
          direction={{ base: "column", lg: "row" }}
          align={{ base: "stretch", lg: "center" }}
          justify="space-between"
        >
          <Flex gap={3} wrap="wrap" align="center" flex="1" minW={0}>
            <InputGroup w={{ base: "100%", sm: "260px" }}>
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faSearch} color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="ค้นหาประเภทปศุสัตว์..."
                value={searchTerm}
                onChange={handleSearch}
                colorScheme="iBlue"
              />
            </InputGroup>
            <Select
              w={{ base: "100%", sm: "160px" }}
              value={filterStatus}
              onChange={handleStatusFilter}
              placeholder="เลือกสถานะ"
              colorScheme="iBlue"
            >
              <option value="">ทั้งหมด</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </Select>
            <IconButton.Clear
              onClick={handleClearSearch}
              label="ล้าง"
              w={{ base: "100%", sm: "auto" }}
            />
          </Flex>
          {hasEditPermission && (
            <IconButton.Add
              onClick={handleAddNew}
              label="เพิ่มข้อมูลใหม่"
              w={{ base: "100%", lg: "auto" }}
            />
          )}
        </Flex>

        {isLoading ? (
          <Flex justify="center" py={8}>
            <Spinner size="lg" />
          </Flex>
        ) : livestockTypes.length === 0 ? (
          <Text fontSize={{ base: "md", md: "lg" }} color="gray.500" textAlign="center" w="100%" py={8}>
            ยังไม่มีข้อมูลประเภทปศุสัตว์
          </Text>
        ) : filteredLivestockTypes.length === 0 ? (
          <Text fontSize={{ base: "md", md: "lg" }} color="gray.500" textAlign="center" w="100%" py={8}>
            ไม่พบข้อมูลที่ค้นหา
          </Text>
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={filteredLivestockTypes}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        )}
      </VStack>

      {/* Modal สำหรับเพิ่ม/แก้ไขข้อมูลประเภทปศุสัตว์ */}
      <Modal
        isOpen={showForm}
        onClose={handleCancel}
        size={{ base: "full", md: "2xl", lg: "3xl" }}
        scrollBehavior="inside"
        isCentered
      >
        <ModalOverlay />
        <ModalContent maxH={{ base: "100vh", md: "90vh" }} my={{ base: 0, md: 4 }}>
          <ModalHeader
            bg="iBlue.600"
            color="white"
            borderTopRadius={{ base: 0, md: "md" }}
            py={{ base: 3, md: 4 }}
          >
            <Heading size={{ base: "sm", md: "md" }} fontWeight="normal" pr={8}>
              {hasEditPermission
                ? isEditing
                  ? "แก้ไขข้อมูลประเภทปศุสัตว์"
                  : "เพิ่มข้อมูลประเภทปศุสัตว์"
                : "รายละเอียดประเภทปศุสัตว์"}
            </Heading>
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody py={{ base: 4, md: 6 }} px={{ base: 3, md: 6 }}>
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
              gap={{ base: 4, md: 6 }}
            >
              <GridItem>
                <FormControl
                  isRequired
                  isInvalid={!!errors.LivestockTypeID}
                  isReadOnly={!hasEditPermission}
                  isDisabled={isEditing && hasEditPermission}
                >
                  <FormLabel fontSize={{ base: "sm", md: "md" }}>รหัสประเภทปศุสัตว์</FormLabel>
                  <Input
                    name="LivestockTypeID"
                    value={formData.LivestockTypeID}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.LivestockTypeID}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel fontSize={{ base: "sm", md: "md" }}>สถานะการใช้งาน</FormLabel>
                  <Flex align="center" gap={3} minH="40px">
                    <StatusActive status={formData.Status === "1"} />
                    <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500">
                      {isEditing
                        ? "ปรับได้จากปุ่มปิด/เปิดการใช้งานด้านล่าง"
                        : "ข้อมูลที่สร้างใหม่จะเปิดการใช้งานทันที"}
                    </Text>
                  </Flex>
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl
                  isRequired
                  isInvalid={!!errors.LivestockTypeName}
                  isReadOnly={!hasEditPermission}
                >
                  <FormLabel fontSize={{ base: "sm", md: "md" }}>ชื่อประเภทปศุสัตว์</FormLabel>
                  <Input
                    name="LivestockTypeName"
                    value={formData.LivestockTypeName}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.LivestockTypeName}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl
                  isRequired
                  isInvalid={!!errors.LivestockTypeNameEN}
                  isReadOnly={!hasEditPermission}
                >
                  <FormLabel fontSize={{ base: "sm", md: "md" }}>
                    ชื่อประเภทปศุสัตว์ (ภาษาอังกฤษ)
                  </FormLabel>
                  <Input
                    name="LivestockTypeNameEN"
                    value={formData.LivestockTypeNameEN}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.LivestockTypeNameEN}</FormErrorMessage>
                </FormControl>
              </GridItem>
              <GridItem colSpan={{ base: 1, md: 2 }}>
                <FormControl isReadOnly={!hasEditPermission}>
                  <FormLabel fontSize={{ base: "sm", md: "md" }}>หมายเหตุ</FormLabel>
                  <Textarea
                    name="Remark"
                    value={formData.Remark}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                </FormControl>
              </GridItem>
            </Grid>
          </ModalBody>
          <ModalFooter gap={2} flexWrap="wrap" justifyContent="flex-end">
            <IconButton.Close
              onClick={handleCancel}
              label="ปิด"
              flex={{ base: "1 1 100%", sm: "0 0 auto" }}
            />
            {hasEditPermission &&
              isEditing &&
              (formData.Status === "1" ? (
                <IconButton.Cancel
                  onClick={handleToggleStatus}
                  label="ปิดการใช้งาน"
                  flex={{ base: "1 1 100%", sm: "0 0 auto" }}
                />
              ) : (
                <IconButton.Approved
                  onClick={handleToggleStatus}
                  label="เปิดการใช้งาน"
                  flex={{ base: "1 1 100%", sm: "0 0 auto" }}
                />
              ))}
            {hasEditPermission && (
              <IconButton.Save
                onClick={handleSave}
                isLoading={isLoading}
                flex={{ base: "1 1 100%", sm: "0 0 auto" }}
              />
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default LivestockTypeManagement;